'use client';
import './desktop.css';
import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { Expand, Hand, ImageOff, MessageCircleQuestion, MessageSquareText, Monitor, MoreHorizontal, Power, ShieldCheck, X } from 'lucide-react';
import type { DesktopPreviewAction, DesktopPreviewProps } from 'elowen-plugin-ui-kit';
import { apiErrorMessage } from '../../lib/elowenClient';
import { useTranslation } from '../../lib/i18n';
import { Button } from '../ui/Button';
import { ConnectionStatus } from '../ui/ConnectionStatus';
import { Modal } from '../ui/Modal';
import { Spinner } from '../ui/states';
import { useToast } from '../ui/Toast';
import { useVncSurface } from './useVncSurface';
import { useDesktopActionMarker, useDesktopActionRing } from './useDesktopActionMarker';
import { usePersistentState } from '../../lib/usePersistentState';
import { DropdownMenu, DropdownMenuContent, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuTrigger } from '../ui/shadcn/dropdown-menu';

const PICTURE_MODES = ['quality', 'speed'] as const;

type Tone = 'success' | 'warning' | 'danger' | 'accent' | 'muted';

/** One control on the glass. The shared `IconButton` is a bordered SQUARE by design, which is right beside
 *  a table cell and wrong on a translucent disc: it drew two frames around one control. This is the same
 *  affordance with one shape, a round ghost icon button that carries the glass itself when it stands alone
 *  on the image and stays transparent inside the controls pill. Keyboard focus is a ring, never a border.
 *
 *  Its BOX is the shared button scale (`Button` at `size="icon"`: 36px, and 44px on a coarse pointer), not a
 *  height of this file's own; the classes below are only the round glass chrome the dock needs. It used to
 *  declare 28px, which left the pill measuring 28 beside the 36px claim button next to it. */
function GlassButton({ icon: Icon, label, onClick, disabled, tone, className = '' }: {
  icon: DesktopPreviewAction['icon']; label: string; onClick?: () => void; disabled?: boolean; tone?: 'danger'; className?: string;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className={`desktop-preview__icon rounded-full ${className}`.trim()}
      data-tone={tone}
      aria-label={label}
      title={label}
      onClick={onClick}
      disabled={disabled}
    >
      <Icon size={14} aria-hidden />
    </Button>
  );
}

/** The shared desktop presentation: a small tile that enlarges into one aspect-fitted raised surface, with
 *  the status and claim controls every desktop needs. Published as `components.DesktopPreview`; Browser and
 *  the Sandbox Project desktop are its two producers.
 *
 *  The producer keeps what it alone knows (what the session is, what the agent is doing, its navigation,
 *  its stop confirmation) and passes it through typed props. This component owns the single viewer: one
 *  canvas, reparented between the tile and the expanded surface rather than dialed twice, because x11vnc
 *  encodes the FULL framebuffer per client and a second thumbnail connection would pay what the big one
 *  pays. A supplied still owns every collapsed tile; only expansion dials its framebuffer. Without
 *  a still, wide tiles remain live and phone tiles say how to open the screen.
 *
 *  The expanded surface is the shared bare `Modal`, so the overlay stack, inert isolation, focus trap,
 *  Escape, scroll lock and the return of focus are the app's own. A confirmation the producer mounts in
 *  `power.dialog` sits inside it and joins the same stack. */
export function DesktopPreview({ control, preview, title, placement, controlsEnabled = true, still, mobileInput = false,
  identity, caption, actionText, navigation, narration, pendingInput, power }: DesktopPreviewProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const [pictureMode, setPictureMode] = usePersistentState('elowen-desktop-picture-mode', 'quality', PICTURE_MODES);
  const { lease, stream, setViewConnected } = control;
  const { expanded, setExpanded, wide } = preview;
  const marker = useDesktopActionMarker(stream.action, actionText);
  const ring = useDesktopActionRing(stream.action);
  // A supplied still owns the collapsed picture at every width. Only expansion requests its framebuffer, and
  // a lease we hold keeps it: the claim lives only as long as a framebuffer socket of this view, so a tile
  // collapsed while in control stays live and keeps offering the hand-back.
  const liveView = expanded || (!still && preview.liveView) || !!lease;
  /** Where the one live canvas is parked: the tile while collapsed, the raised surface while open. The canvas
   *  node itself is never rebuilt, only reparented. */
  const [tileSlot, setTileSlot] = useState<HTMLElement | null>(null);
  const [surfaceSlot, setSurfaceSlot] = useState<HTMLElement | null>(null);
  const [pending, setPending] = useState<'takeover' | 'release' | null>(null);
  const afterCollapse = useRef<(() => void) | null>(null);
  const [speechHidden, setSpeechHidden] = useState(false);
  const speechTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The canvas covers the transcript, so carry the agent's bounded reply above it.
  const speech = (narration ?? '').trim();
  const visibleSpeech = speechHidden ? '' : speech;
  // Only a label and a way back cross the pending-question boundary.
  const waiting = pendingInput ?? null;

  useEffect(() => () => {
    if (speechTimer.current) clearTimeout(speechTimer.current);
  }, []);
  useEffect(() => {
    if (speechTimer.current) { clearTimeout(speechTimer.current); speechTimer.current = null; }
    // The host clears narration on the next user turn. That empty edge is the message boundary: a manually
    // dismissed or expired line stays hidden while the same reply streams, then the next reply may show.
    if (!speech) { setSpeechHidden(false); return; }
    if (speechHidden) return;
    speechTimer.current = setTimeout(() => {
      speechTimer.current = null;
      setSpeechHidden(true);
    }, 10_000);
    return () => {
      if (speechTimer.current) { clearTimeout(speechTimer.current); speechTimer.current = null; }
    };
  }, [speech, speechHidden]);

  const vncEnabled = !stream.closed && liveView;
  const keyboardReachesRemote = expanded && (wide || mobileInput) && controlsEnabled && control.interactive;
  const vnc = useVncSurface({
    slot: expanded ? surfaceSlot : tileSlot,
    ticket: control.ticket,
    connectionKey: control.connectionKey,
    interactive: keyboardReachesRemote,
    enabled: vncEnabled,
    pictureMode,
  });
  const painting = vnc.connectionState?.freshness === 'fresh' && vnc.connectionState.readiness === 'ready';
  const controlReady = stream.connectionState?.freshness === 'fresh' && stream.connectionState.readiness === 'ready';
  /** A control ACTION that failed while the stream itself stayed ready — a refused takeover, a heartbeat
   *  the server no longer honours. Nothing else on this surface carries it: the connection lane below only
   *  exists while the stream is not ready, and a healthy transport reported as dropped would be a lie.
   *  The stream error is a bare diagnostic (`Chetty 409 on …`), so the reader gets this surface's sentence. */
  const actionError = controlReady && !stream.closed && !!stream.error;
  useEffect(() => {
    setViewConnected(painting);
    return () => setViewConnected(false);
  }, [setViewConnected, painting]);

  const collapse = (after?: () => void): void => {
    afterCollapse.current = after ?? null;
    setExpanded(false);
  };
  useEffect(() => {
    // Handing the reader on to something that takes focus (a question card) is TWO moves in a fixed order:
    // the overlay closes first, and only then may the next target focus. Radix restores focus to the opener
    // from a timer queued while the overlay unmounts, so a callback run synchronously here would be undone
    // by it. This timer is queued after that one and runs after it.
    if (expanded) return undefined;
    const after = afterCollapse.current;
    if (!after) return undefined;
    afterCollapse.current = null;
    const timer = setTimeout(after, 0);
    return () => clearTimeout(timer);
  }, [expanded]);

  const takeoverRequested = stream.control.state === 'agent' && stream.control.reason === 'requested';
  /** The agent handed this session over and is parked until it is handed back. The server keeps saying so
   *  through the claim, which is what separates "the agent is waiting on you" from a takeover a person
   *  started for their own reasons. */
  const agentAwaitingReturn = stream.control.state === 'user' && stream.control.reason === 'handed_over' && !!lease;
  const state = stream.closed ? 'closed' : stream.control.state;
  /** A parked agent is a STATE and outranks whatever action it finished before handing over: that stale
   *  line would otherwise read as though it were still working. A FAILED action outranks both, because
   *  "waiting for you" over a claim that the server refused would describe a handover that never came. */
  const markerText = stream.action ? marker?.text : actionText;
  const action = actionError ? t.desktop.requestFailed
    : (agentAwaitingReturn ? t.desktop.agentWaiting : markerText ?? (takeoverRequested ? t.desktop.statusWaitingForUser : null));

  const status = useMemo((): { tone: Tone; label: string } => {
    const d = t.desktop;
    if (stream.closed) return { tone: 'muted', label: d.statusClosed };
    // A still that could not be renewed is not a session in trouble, so it is not the same red as one.
    if (!liveView && still?.state === 'stalled') return { tone: 'warning', label: d.statusStalled };
    if (actionError) return { tone: 'danger', label: d.requestFailed };
    if (state === 'handover-pending') return { tone: 'warning', label: d.statusHandoverPending };
    if (state === 'user') return { tone: 'accent', label: lease ? d.statusYouControl : d.statusUserControl };
    if (takeoverRequested) return { tone: 'warning', label: d.statusWaitingForUser };
    const known = controlReady || !liveView && still?.state === 'ready';
    return known ? { tone: 'success', label: d.statusAgentControl } : { tone: 'warning', label: d.statusConnecting };
  }, [actionError, controlReady, lease, liveView, state, still?.state, stream.closed, t, takeoverRequested]);

  const run = async (name: 'takeover' | 'release', operation: () => Promise<void>): Promise<void> => {
    setPending(name);
    try { await operation(); }
    catch (error) { toast.toast(apiErrorMessage(error) || t.desktop.requestFailed, 'error'); }
    finally { setPending(null); }
  };

  const aspect = vnc.aspect ?? still?.aspect ?? null;
  const aspectStyle = aspect ? { '--desktop-aspect': String(aspect) } as CSSProperties : undefined;
  const viewerAttempting = vnc.state !== 'failed'
    && (!vnc.connectionState || vnc.connectionState.retry?.phase === 'attempting');
  const connectingLabel = vnc.state === 'viewer_limit' ? t.desktop.statusViewerLimit
    : vnc.state === 'disconnected' ? t.desktop.statusDisconnected
      : viewerAttempting ? t.desktop.connecting : t.desktop.unavailable;
  const showViewerConnection = vncEnabled && !painting;
  const showControlConnection = !stream.closed && !controlReady && (!!stream.connectionState || !!stream.error);
  // Recovery buttons live outside the tile's open button, and move into the modal rather than duplicate.
  const connections = showViewerConnection || showControlConnection ? (
    <div className="flex min-w-0 flex-col gap-1 rounded-md bg-background/90 p-2">
      {showViewerConnection ? <ConnectionStatus label={t.desktop.viewerConnection}
        connectionState={vnc.connectionState} onRetry={vnc.retryConnection} /> : null}
      {showControlConnection ? <ConnectionStatus label={t.desktop.controlConnection}
        connectionState={stream.connectionState} onRetry={stream.retryConnection} /> : null}
    </div>
  ) : null;

  /** The mark on the glass: the session's state in one dot and, beside it, what the agent is doing to the
   *  desktop right now as one truncated line. The collapsed tile and the raised surface draw the same timed
   *  line; while the surface is open the tile behind it stays at the dot, so the line is not on screen twice. */
  const activity = (raised: boolean): ReactNode => {
    const line = raised || !expanded ? action : null;
    return (
      <div className={`desktop-preview__activity ${line ? 'has-action' : ''}`}>
        <span className="desktop-preview__dot" data-tone={status.tone} aria-hidden />
        <span className="sr-only">{status.label}</span>
        {line ? <span className="truncate">{line}</span> : null}
      </div>
    );
  };

  /** The live picture. noVNC binds the pointer and keyboard to its OWN canvas inside this box, so there are
   *  no input handlers here: attaching some as well would send every gesture twice. */
  const liveCanvas = (raised: boolean) => (
    // The raised surface owns its gestures whether or not the keyboard reaches the machine: a pinch on a
    // watch-only phone view must not zoom the page behind it, which is what `data-interactive` stops.
    <div className="desktop-preview__canvas" role="group" aria-label={title} data-interactive={raised ? 'true' : undefined}>
      {/* Always rendered: the client needs somewhere to mount before it can connect. */}
      <div className="desktop-preview__slot" ref={raised ? setSurfaceSlot : setTileSlot} />
      {painting ? null : (
        <div className="desktop-preview__waiting" role="status" aria-live="polite">
          {stream.closed || !viewerAttempting ? <Monitor size={22} aria-hidden /> : <Spinner size="lg" />}
          <span>{stream.closed ? t.desktop.statusClosed : connectingLabel}</span>
        </div>
      )}
      {activity(raised)}
    </div>
  );

  /** The tile below the phone breakpoint: the producer's still, or an instruction to open the screen. There
   *  is deliberately no fake picture: a tile with nothing to show says how to get the screen. */
  const quietCanvas = () => (
    <div className="desktop-preview__canvas desktop-preview__canvas--still" role="group" aria-label={title}>
      {!stream.closed && still?.dataUrl ? (
        // The frame is the picture's own box (the still is fitted, so it can be smaller than the tile), which
        // is what the ring's percentages are measured against.
        <span className="desktop-preview__still-frame" style={still.aspect ? { '--still-aspect': String(still.aspect) } as CSSProperties : undefined}>
          <img className="desktop-preview__still" src={still.dataUrl} alt="" />
          {ring ? <span key={ring.key} className="desktop-preview__ring" aria-hidden style={{ left: `${ring.x * 100}%`, top: `${ring.y * 100}%` }} /> : null}
        </span>
      ) : null}
      {stream.closed ? (
        <div className="desktop-preview__waiting" role="status"><Monitor size={22} aria-hidden /><span>{t.desktop.statusClosed}</span></div>
      ) : still ? (
        still.state === 'ready' ? null : (
          <div className="desktop-preview__waiting" role="status" aria-live="polite">
            {still.state === 'stalled' ? <ImageOff size={16} aria-hidden /> : <Spinner size="lg" />}
            <span>{still.state === 'stalled' ? t.desktop.statusStalled : t.desktop.connecting}</span>
          </div>
        )
      ) : (
        <div className="desktop-preview__waiting">
          <Monitor size={22} aria-hidden />
          <span>{t.desktop.openToView}</span>
        </div>
      )}
      {activity(false)}
    </div>
  );

  /** One control for who is driving, in the same shape on the tile and on the expanded surface. A read-only
   *  view claims nothing, so it offers nothing.
   *
   *  Claiming needs pixels: the server refuses a takeover from a viewer with no framebuffer connection, so a
   *  tile that shows a still claims nothing itself, and one still connecting offers the claim disabled. When
   *  the agent asks for a person, a still tile offers the one way there instead: opening the screen, where
   *  the connected view takes the requested control. Once WE hold the lease (`user`, or the handover that is
   *  waiting for the agent's last action to stop) the only thing left to offer is handing it back: a second
   *  claim would be refused, and a pending handover is exactly the state in which the reader must still be
   *  able to give up. */
  const claimControl = (): ReactNode => {
    if (!controlsEnabled) return null;
    if (lease && (state === 'user' || state === 'handover-pending')) {
      return (
        <Button
          variant="accent"
          icon={ShieldCheck}
          // The pulse marks a session that is WAITING on this button, and only that.
          className={agentAwaitingReturn ? 'desktop-preview__control--waiting' : undefined}
          onClick={() => { void run('release', control.releaseControl); }}
          disabled={pending !== null}
        >
          {t.desktop.returnToAgent}
        </Button>
      );
    }
    // Somebody else's lease, or one pending that is not ours.
    if (state === 'user' || state === 'handover-pending') return <Button variant="ghost" icon={Hand} disabled>{t.desktop.controlledElsewhere}</Button>;
    if (lease) return null;
    if (!liveView) {
      return takeoverRequested && !stream.closed ? (
        <Button variant="accent" icon={Hand} className="desktop-preview__control--waiting" onClick={() => setExpanded(true)}>
          {t.desktop.takeControl}
        </Button>
      ) : null;
    }
    return <Button variant="ghost" icon={Hand} onClick={() => { void run('takeover', control.takeControl); }} disabled={pending !== null || stream.closed || !painting}>{t.desktop.takeControl}</Button>;
  };

  const identityLabel = identity ? <span className="desktop-preview__identity">{identity}</span> : null;
  // Under the collapsed tile the producer's caption takes the identity's place. The expanded controls carry
  // no name: the raised view is the screen itself, and the dialog's title still names it for assistive tech.
  const tileCaption = caption ?? identityLabel;
  const claim = claimControl();
  /** What the agent just did, announced once it has finished; announcing "started" would only narrate the next
   *  sentence the reader is about to hear as "done". There is ONE such region, never two: the collapsed tile
   *  carries it beside the tile button (the button's own name is all a screen reader takes from its inside),
   *  and while the surface is open it moves into the dialog, because everything outside the dialog is inert. */
  const announce = (
    <span className="desktop-preview__announce sr-only" role="status" aria-live="polite">
      {stream.action && marker && marker.status !== 'started' ? marker.text : ''}
    </span>
  );

  return (
    <section
      className="desktop-preview"
      ref={still?.ref}
      style={aspectStyle}
      data-placement={placement}
      // The marker the chat surface measures to reserve room for a docked tile. Only a chat tile carries it,
      // and an expanded one declares itself so its reservation is released while it is raised.
      data-chat-dock={placement === 'chat' ? '' : undefined}
      data-expanded={expanded ? 'true' : undefined}
      aria-label={title}
    >
      {/* The tile is the control that opens the surface: one target, no chrome around it. */}
      <Button variant="ghost" className="desktop-preview__tile" onClick={() => setExpanded(true)} aria-label={`${t.desktop.enlarge}: ${title}`}>
        {liveView ? liveCanvas(false) : quietCanvas()}
        <span className="desktop-preview__expand" aria-hidden><Expand size={13} /></span>
      </Button>
      {expanded ? null : <>{announce}{connections}</>}
      {tileCaption || claim ? (
        <div className="desktop-preview__caption">
          <span className="min-w-0 flex-1">{tileCaption}</span>
          {claim}
        </div>
      ) : null}

      {expanded ? (
        <Modal title={title} onClose={() => setExpanded(false)} chrome="bare" presentation="center" scrim="soft">
          <div className="desktop-preview__surface" style={aspectStyle}>
            {liveCanvas(true)}
            <GlassButton icon={X} label={t.desktop.closeView} onClick={() => collapse()} className="desktop-preview__dismiss" />
            {/* One bottom column, so what the producer says and the controls stack without either being
                placed against a guessed offset of the other. The column lets the pointer through; only the
                controls take it back. */}
            <div className="desktop-preview__dock">
              <div className="desktop-preview__notices">
                {/* One persistent region, so a question that arrives while the canvas is already open is
                    announced rather than appearing silently behind it. */}
                <span className="sr-only" role="status" aria-live="polite">{waiting ? waiting.label : ''}</span>
                {announce}
                {connections}
                {visibleSpeech ? (
                  <p className="desktop-preview__narration" role="status" aria-live="polite" aria-atomic="true">
                    {/* Whose words these are, in one mark rather than a line of copy explaining it. */}
                    <MessageSquareText className="desktop-preview__narration-icon" size={13} aria-hidden />
                    {/* The clamp lives on the inner box: a padded element clips its overflow at the PADDING
                        edge, so a third line paints into the bottom padding and is cut in half instead of
                        hidden. Padding out here, clamp in there. */}
                    <span className="desktop-preview__narration-text">{visibleSpeech}</span>
                    {/* The shared ghost icon step: the subtitle's own 1.4rem box, its inline margins and its
                        round hover come from `desktop-preview__narration-dismiss`, which is unlayered and so
                        outranks the button's utilities. */}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="desktop-preview__narration-dismiss"
                      onClick={() => setSpeechHidden(true)}
                      aria-label={t.desktop.dismissNarration}
                      title={t.desktop.dismissNarration}
                    >
                      <X size={12} aria-hidden />
                    </Button>
                  </p>
                ) : null}
                {waiting ? (
                  // The reveal runs only after the canvas has closed and focus is back on the tile, so the
                  // closing overlay cannot pull focus away from the question card it just revealed.
                  <Button variant="ghost" className="desktop-preview__question" onClick={() => collapse(waiting.reveal)}>
                    <MessageCircleQuestion size={15} aria-hidden />
                    <span className="truncate">{waiting.label}</span>
                  </Button>
                ) : null}
              </div>
              <div className="desktop-preview__controls">
                {navigation?.map((item) => (
                  <GlassButton key={item.id} icon={item.icon} label={item.label} onClick={item.onSelect} disabled={item.disabled} />
                ))}
                {claim}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="desktop-preview__icon" aria-label={t.desktop.moreActions} title={t.desktop.moreActions}>
                      <MoreHorizontal size={14} aria-hidden />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" side="top" aria-label={t.desktop.moreActions}>
                    <DropdownMenuRadioGroup value={pictureMode} onValueChange={(value) => {
                      if (value === 'quality' || value === 'speed') setPictureMode(value);
                    }}>
                      <DropdownMenuRadioItem value="quality">{t.desktop.pictureQuality}</DropdownMenuRadioItem>
                      <DropdownMenuRadioItem value="speed">{t.desktop.pictureSpeed}</DropdownMenuRadioItem>
                    </DropdownMenuRadioGroup>
                  </DropdownMenuContent>
                </DropdownMenu>
                {power ? <GlassButton icon={Power} label={power.label} tone="danger" onClick={power.onSelect} disabled={power.disabled} /> : null}
              </div>
            </div>
          </div>
          {power?.dialog}
        </Modal>
      ) : null}
    </section>
  );
}
